import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  FaGoogle,
  FaEye,
  FaLink,
  FaUsers,
  FaBook,
  FaBell,
  FaFileAlt,
  FaCheckCircle,
  FaStream,
  FaTasks,
  FaUserFriends,
  FaCalendar,
  FaComment,
  FaPaperclip,
  FaVideo,
  FaImage,
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaExternalLinkAlt,
  FaChevronDown,
  FaChevronUp,
  FaSearch,
  FaEnvelope,
  FaClock,
  FaFolder,
  FaSpinner, FaUserCircle, FaFileArchive, FaFile
} from "react-icons/fa";
import "./GoogleClassroom.css";

axios.defaults.withCredentials = true;

const GoogleClassroom = () => {
  const [googleAuthenticated, setGoogleAuthenticated] = useState(false);
  const [googleCourses, setGoogleCourses] = useState([]);
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const [courseDetails, setCourseDetails] = useState(null);
  const [activeTab, setActiveTab] = useState("stream");
  const [expandedAnnouncements, setExpandedAnnouncements] = useState({});
   const [selectedTopic, setSelectedTopic] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  

  // On load: check for google redirect and auth status
  useEffect(() => {
    checkUrlForGoogleAuth();
    checkGoogleAuthStatus();
  }, []);

  // Handle Google redirect result
  const checkUrlForGoogleAuth = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const googleAuth = urlParams.get("googleAuth");
    const user = urlParams.get("user");
    const error = urlParams.get("error");

    if (googleAuth === "success") {
      toast.success(`Google Classroom connected successfully! Welcome ${user}`);
      window.history.replaceState({}, document.title, window.location.pathname);
      setTimeout(() => {
  checkGoogleAuthStatus();
}, 500);
    } else if (googleAuth === "error" || error) {
      toast.error("Failed to connect Google Classroom");
    }
  };

  // Check backend token status - FIXED ENDPOINT
  const checkGoogleAuthStatus = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/google-classroom/auth-status`
      );

      console.log("Auth status response:", response.data);
      const isAuth = response.data.authenticated || false;
      setGoogleAuthenticated(isAuth);

      if (isAuth) {
        fetchGoogleCourses();
      }
    } catch (err) {
      console.error("Auth check error:", err);
      setGoogleAuthenticated(false);
      toast.error("Failed to check authentication status");
    }
  };

  // Start Google OAuth - FIXED ENDPOINT
  const handleGoogleAuth = () => {
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/google-classroom/auth`;
  };

  // Force Google to show account chooser again
  const handleGoogleReAuth = () => {
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/google-classroom/auth?prompt=select_account`;
  };

  // Fetch courses - FIXED ENDPOINT
  const fetchGoogleCourses = async () => {
    setLoadingGoogle(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/google-classroom/courses`
      );

      console.log("Courses response:", response.data);

      if (response.data.courses) {
        setGoogleCourses(response.data.courses);
        toast.success(`Found ${response.data.courses.length} courses`);
      } else if (Array.isArray(response.data)) {
        setGoogleCourses(response.data);
      } else {
        setGoogleCourses([]);
      }
    } catch (err) {
      console.error("Courses fetch error:", err);
      toast.error("Failed to fetch Google Classroom courses");
      setGoogleCourses([]);
    } finally {
      setLoadingGoogle(false);
    }
  };

  // Fetch course details - FIXED FOR NEW DATA STRUCTURE
  const fetchCourseDetails = async (courseId) => {
    setLoadingGoogle(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/google-classroom/courses/${courseId}`
      );
      
      console.log("Course details response:", response.data);
      
      if (response.data.success) {
        setCourseDetails(response.data);
        setActiveTab("stream");
        toast.success("Course details loaded!");
      } else if (response.data.courseInfo) {
        // If old structure, create new structure
        const transformedData = transformOldToNewFormat(response.data);
        setCourseDetails(transformedData);
        setActiveTab("stream");
        toast.success("Course details loaded!");
      } else {
        toast.error("Failed to fetch course details");
      }
    } catch (err) {
      console.error("Course details error:", err);
      toast.error("Failed to fetch course details");
    } finally {
      setLoadingGoogle(false);
    }
  };

  // Transform old API response to new structure
  const transformOldToNewFormat = (oldData) => {
    return {
      success: true,
      courseInfo: oldData.courseInfo || {
        name: "Unknown Course",
        section: "No section",
        enrollmentCode: "No code",
        alternateLink: "#"
      },
      stream: {
        items: oldData.announcements?.map(ann => ({
          id: ann.id,
          type: 'announcement',
          text: ann.text || '',
          creationTime: ann.creationTime,
          creator: { name: "Teacher" }
        })) || [],
        count: oldData.announcements?.length || 0
      },
      classwork: {
        allItems: oldData.assignments?.map(ass => ({
          id: ass.id,
          type: 'assignment',
          title: ass.title || 'Untitled',
          description: ass.description || '',
          dueDate: ass.dueDate,
          maxPoints: ass.maxPoints || 0,
          materials: ass.materials || []
        })) || [],
        count: oldData.assignments?.length || 0,
        stats: {
          totalAssignments: oldData.assignments?.length || 0,
          totalMaterials: oldData.studyMaterials?.length || 0
        }
      },
      people: {
        teachers: oldData.teachers?.map(teacher => ({
          userId: teacher.userId,
          fullName: teacher.fullName || 'Unknown Teacher',
          email: teacher.email || 'No email',
          photoUrl: teacher.photoUrl
        })) || [],
        students: oldData.students?.map(student => ({
          userId: student.userId,
          fullName: student.fullName || 'Unknown Student',
          email: student.email || 'No email',
          photoUrl: student.photoUrl
        })) || [],
        studentCount: oldData.students?.length || 0,
        teacherCount: oldData.teachers?.length || 0
      },
      stats: {
        totalStudents: oldData.stats?.totalStudents || 0,
        totalAssignments: oldData.stats?.totalAssignments || 0,
        totalAnnouncements: oldData.stats?.totalAnnouncements || 0,
        totalMaterials: oldData.stats?.totalMaterials || 0
      }
    };
  };

  // Disconnect Google
  const disconnectGoogle = async () => {
    if (!window.confirm("Disconnect Google Classroom?")) return;

    try {
      await axios.get(`${process.env.REACT_APP_BACKEND_URL}/google-classroom/disconnect`);
      setGoogleAuthenticated(false);
      setGoogleCourses([]);
      setCourseDetails(null);
      toast.success("Google Classroom disconnected");
    } catch {
      toast.error("Failed to disconnect");
    }
  };

  // Toggle announcement expansion
  const toggleAnnouncement = (id) => {
    setExpandedAnnouncements(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Render Stream Tab
// Render Stream Tab
// Render Stream Tab - UPDATED WITH POST BUTTON
const renderStreamTab = () => {
  if (!courseDetails?.stream?.items?.length) {
    return (
      <div className="stream-tab">
        <div className="stream-header">
          <h4>Class Stream</h4>
          {/* ADDED: POST ANNOUNCEMENT BUTTON */}
          {courseDetails?.courseInfo?.alternateLink && (
            <button 
              className="btn-primary"
              onClick={() => window.open(courseDetails.courseInfo.alternateLink, '_blank')}
            >
              <FaBell /> Post Announcement
            </button>
          )}
        </div>
        <div className="empty-state">No stream content</div>
      </div>
    );
  }

  // Debug: Show total count
  console.log('Total stream items:', courseDetails.stream.count);
  console.log('Stream items sample:', courseDetails.stream.items.slice(0, 5));

  return (
    <div className="stream-tab">
      {/* UPDATED HEADER WITH POST BUTTON */}
      <div className="stream-header">
        <div className="header-left">
          <h4>Class Stream ({courseDetails.stream.count} items total)</h4>
          <div className="stream-stats">
            <span className="stat-badge">{courseDetails.stream.announcementsCount || 0} Announcements</span>
            <span className="stat-badge">{courseDetails.stream.materialsCount || 0} Materials</span>
            <span className="stat-badge">{courseDetails.stream.assignmentsCount || 0} Assignments</span>
          </div>
        </div>
        <div className="header-right">
          {/* ADDED: POST ANNOUNCEMENT BUTTON */}
          {courseDetails?.courseInfo?.alternateLink && (
            <button 
              className="btn-primary"
              onClick={() => window.open(courseDetails.courseInfo.alternateLink, '_blank')}
            >
              <FaBell /> Post Announcement
            </button>
          )}
        </div>
      </div>

      <div className="stream-items">
        {courseDetails.stream.items.map((item, index) => (
          <div key={item.id || index} className={`stream-item ${item.type}`}>
            
            {/* HEADER */}
            <div className="stream-item-header">
              <div className="creator-info">
                <FaUserCircle />
                <strong>{item.creator || "Teacher"}</strong>
                <span className="item-type">
                  {item.displayText || 
                   (item.type === 'material' ? 'posted a new material' : 
                    item.type === 'assignment' ? 'posted an assignment' : 
                    'posted an announcement')}
                </span>
              </div>
              <span className="item-date">{item.formattedDate}</span>
            </div>

            {/* CONTENT */}
            <div className="stream-content">
              {/* TITLE */}
              {item.title && (
                <h5>{item.title}</h5>
              )}
              
              {/* TEXT/DESCRIPTION */}
              {item.text && (
                <div className="announcement-text">
                  {item.text}
                </div>
              )}
              
              {item.description && !item.text && (
                <p className="item-description">{item.description}</p>
              )}
              
              {/* FOR ASSIGNMENTS - SHOW DUE DATE */}
              {item.dueDate && (
                <div className="due-info">
                  <FaCalendar /> Due: {formatDate(item.dueDate)}
                  {item.maxPoints && ` • ${item.maxPoints} points`}
                </div>
              )}

              {/* ATTACHMENTS */}
              {item.attachments && item.attachments.length > 0 && (
                <div className="attachments-section">
                  <h6>
                    <FaPaperclip /> 
                    {item.attachments.length} attachment{item.attachments.length > 1 ? 's' : ''}
                  </h6>
                  <div className="attachments-grid">
                    {item.attachments.map((attachment, idx) => (
                      <div key={idx} className="attachment-item">
                        <div className="attachment-icon">
                          {attachment.type === 'youtubeVideo' ? <FaVideo /> :
                           attachment.type === 'driveFile' ? 
                             (attachment.isCompressed ? <FaFileArchive /> : <FaFile />) :
                           <FaLink />}
                        </div>
                        <div className="attachment-info">
                          <a 
                            href={attachment.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="attachment-link"
                          >
                            {attachment.title}
                            {attachment.isCompressed && " (Compressed)"}
                            {attachment.mimeType && (
                              <span className="file-type"> - {attachment.mimeType}</span>
                            )}
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

          
            
          </div>
        ))}
      </div>
    </div>
  );
};



 // Render Classwork Tab - UPDATED WITH GOOGLE CLASSROOM STRUCTURE
const renderClassworkTab = () => {
 
  
  if (!courseDetails?.classwork?.allItems?.length) {
    return (
      <div className="empty-state">
        <FaTasks size={48} color="#ddd" />
        <h4>No classwork available</h4>
        <p>This course doesn't have any assignments or materials yet.</p>
      </div>
    );
  }

  // Function to handle View button click
  const handleViewItem = (item) => {
    if (!item) return;
    
    // If item has attachments, open the first accessible one
    if (item.attachments && item.attachments.length > 0) {
      const accessibleAttachment = item.attachments.find(att => att.url && att.url !== '#');
      if (accessibleAttachment) {
        window.open(accessibleAttachment.url, '_blank');
        return;
      }
    }
    
    // Try to open in Google Classroom
    if (courseDetails.courseInfo.id && item.id) {
      const classroomLink = `https://classroom.google.com/c/${courseDetails.courseInfo.id}/a/${item.id}`;
      window.open(classroomLink, '_blank');
      return;
    }
    
    // Show error if no link available
    toast.error('Unable to open this item. No accessible link found.');
  };

  // Create topic list from image
  const defaultTopics = [
    { id: 'all', name: 'All topics', icon: <FaFolder /> },
    { id: 'test-papers', name: 'TEST PAPERS', icon: <FaFileAlt /> },
    { id: 'daily-tracker', name: 'Daily Student Tracker', icon: <FaCalendar /> },
    { id: 'rhymes', name: 'Rhymes Audio File', icon: <FaFile /> },
    { id: 'curriculum', name: 'Curriculum', icon: <FaBook /> },
    { id: 'training', name: 'Training Recordings', icon: <FaVideo /> },
    { id: 'digital-board', name: 'Digital Board Content', icon: <FaImage /> },
    { id: 'year-planner', name: 'Year Planner', icon: <FaCalendar /> },
    { id: 'animated-stories', name: 'UKG ANIMATED STORIES', icon: <FaVideo /> }
  ];

  // Filter items based on selected topic
  const getFilteredItems = () => {
    if (selectedTopic === 'all') {
      return courseDetails.classwork.allItems;
    }
    
    // Map topic IDs to filter criteria
    const topicFilters = {
      'test-papers': (item) => item.title?.toLowerCase().includes('test') || 
                               item.title?.toLowerCase().includes('periodic') ||
                               item.title?.toLowerCase().includes('paper') ||
                               item.title?.toLowerCase().includes('question'),
      'daily-tracker': (item) => item.title?.toLowerCase().includes('tracker') ||
                                item.title?.toLowerCase().includes('daily'),
      'rhymes': (item) => item.title?.toLowerCase().includes('rhymes') ||
                         item.title?.toLowerCase().includes('audio'),
      'curriculum': (item) => item.title?.toLowerCase().includes('curriculum') ||
                             item.title?.toLowerCase().includes('plan') ||
                             item.title?.toLowerCase().includes('syllabus'),
      'training': (item) => item.title?.toLowerCase().includes('training') ||
                           item.title?.toLowerCase().includes('session') ||
                           item.title?.toLowerCase().includes('workshop'),
      'digital-board': (item) => item.title?.toLowerCase().includes('story') ||
                               item.title?.toLowerCase().includes('book') ||
                               item.title?.toLowerCase().includes('digital'),
      'year-planner': (item) => item.title?.toLowerCase().includes('planner') ||
                               item.title?.toLowerCase().includes('year'),
      'animated-stories': (item) => item.title?.toLowerCase().includes('animated') ||
                                   item.title?.toLowerCase().includes('stories')
    };

    const filterFn = topicFilters[selectedTopic];
    return filterFn ? courseDetails.classwork.allItems.filter(filterFn) : courseDetails.classwork.allItems;
  };

  const filteredItems = getFilteredItems();
  
  // Filter by search term
  const searchedItems = searchTerm 
    ? filteredItems.filter(item => {
        const term = searchTerm.toLowerCase();
        return (
          (item.title && item.title.toLowerCase().includes(term)) ||
          (item.description && item.description.toLowerCase().includes(term)) ||
          (item.type && item.type.toLowerCase().includes(term))
        );
      })
    : filteredItems;

  // Group items by type for display
  const materials = searchedItems.filter(item => item.type === 'material' || item.isMaterial);
  const assignments = searchedItems.filter(item => item.type === 'assignment' || item.isAssignment);

  return (
    <div className="classwork-tab">
      {/* HEADER WITH FILTERS */}
      <div className="classwork-header">
        <div className="header-left">
          
        </div>
        
        <div className="header-right">
          {/* SEARCH BAR */}
          <div className="search-box">
            <FaSearch />
            <input
              type="text"
              placeholder="Search classwork..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            {searchTerm && (
              <button 
                className="clear-search"
                onClick={() => setSearchTerm('')}
              >
                ×
              </button>
            )}
          </div>
          
          {/* TOPICS FILTER */}
          <div className="topics-filter">
            <div className="filter-label">
              <FaFolder /> Filter by topic
            </div>
            <select 
              className="topic-select"
              value={selectedTopic}
              onChange={(e) => setSelectedTopic(e.target.value)}
            >
              {defaultTopics.map(topic => (
                <option key={topic.id} value={topic.id}>
                  {topic.name}
                </option>
              ))}
            </select>
          </div>


     <button

              className="btn-primary"

              onClick={() => window.open(courseDetails.courseInfo.alternateLink, '_blank')}

            >

              <FaBell /> Post Classwork 

            </button>
        </div>
      </div>

      {/* CONTENT AREA */}
      <div className="classwork-content">
        

        {/* RIGHT COLUMN - CLASSWORK ITEMS */}
        <div className="classwork-items-container">
          {/* SHOW ACTUAL GOOGLE CLASSROOM ITEMS */}
          {searchedItems.length === 0 ? (
            <div className="no-results">
              <FaSearch size={48} color="#ddd" />
              <h4>No items found</h4>
              <p>Try changing your search or filter criteria</p>
              <button 
                className="btn-secondary"
                onClick={() => {
                  setSearchTerm('');
                  setSelectedTopic('all');
                }}
              >
                Clear filters
              </button>
            </div>
          ) : (
            <>
              {/* MATERIALS SECTION */}
              {materials.length > 0 && (
                <div className="classwork-topic-section">
                  <h3 className="topic-title">
                    <FaBook /> Materials ({materials.length})
                  </h3>
                  {materials.map(item => (
                    <div key={item.id} className="topic-item-card">
                      <div className="item-icon">
                        <FaBook color="#4285f4" />
                      </div>
                      <div className="item-info">
                        <h4>{item.title || 'Untitled Material'}</h4>
                        {item.description && (
                          <p className="item-description">
                            {item.description}
                          </p>
                        )}
                        <div className="item-meta">
                          <span className="item-date">
                            <FaCalendar /> Posted: {formatDate(item.creationTime)}
                          </span>
                          {item.attachments && item.attachments.length > 0 && (
                            <span className="attachments-count">
                              <FaPaperclip /> {item.attachments.length} file{item.attachments.length > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                        
                        {/* ATTACHMENTS */}
                        {item.attachments && item.attachments.length > 0 && (
                          <div className="item-attachments">
                            {item.attachments.map((attachment, idx) => (
                              <div key={idx} className="attachment-item">
                                <div className="attachment-icon">
                                  {attachment.type === 'youtubeVideo' ? <FaVideo color="#ff0000" /> :
                                   attachment.type === 'driveFile' ? 
                                     (attachment.isCompressed ? <FaFileArchive color="#666" /> : <FaFile color="#4285f4" />) :
                                   attachment.type === 'link' ? <FaLink color="#0a66c2" /> :
                                   <FaFile color="#666" />}
                                </div>
                                <div className="attachment-info">
                                  <a 
                                    href={attachment.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="attachment-link"
                                    onClick={(e) => {
                                      if (!attachment.url || attachment.url === '#') {
                                        e.preventDefault();
                                        toast.error('This file is not accessible');
                                      }
                                    }}
                                  >
                                    {attachment.title}
                                    {attachment.isCompressed && " (Compressed)"}
                                  </a>
                                  {attachment.mimeType && (
                                    <span className="file-type">{attachment.mimeType}</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      {/* <div className="item-actions">
                        <button 
                          className="btn-view"
                          onClick={() => handleViewItem(item)}
                        >
                          <FaEye /> View
                        </button>
                        {courseDetails.courseInfo.id && item.id && (
                          <button 
                            className="btn-view"
                            onClick={() => window.open(
                              `https://classroom.google.com/c/${courseDetails.courseInfo.id}/a/${item.id}`,
                              '_blank'
                            )}
                          >
                            <FaGoogle /> Open in Classroom
                          </button>
                        )}
                      </div> */}
                    </div>
                  ))}
                </div>
              )}
              
              {/* ASSIGNMENTS SECTION */}
              {assignments.length > 0 && (
                <div className="classwork-topic-section">
                  <h3 className="topic-title">
                    <FaFileAlt /> Assignments ({assignments.length})
                  </h3>
                  {assignments.map(item => (
                    <div key={item.id} className="topic-item-card">
                      <div className="item-icon">
                        <FaFileAlt color="#ea4335" />
                      </div>
                      <div className="item-info">
                        <h4>{item.title || 'Untitled Assignment'}</h4>
                        {item.description && (
                          <p className="item-description">
                            {item.description}
                          </p>
                        )}
                        <div className="item-meta">
                          <span className="item-date">
                            <FaCalendar /> Posted: {formatDate(item.creationTime)}
                          </span>
                          {item.dueDate && (
                            <span className="due-date">
                              <FaClock /> Due: {formatDate(item.dueDate)}
                            </span>
                          )}
                          {item.maxPoints && (
                            <span className="points">{item.maxPoints} points</span>
                          )}
                          {item.attachments && item.attachments.length > 0 && (
                            <span className="attachments-count">
                              <FaPaperclip /> {item.attachments.length} file{item.attachments.length > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                        
                        {/* ATTACHMENTS */}
                        {item.attachments && item.attachments.length > 0 && (
                          <div className="item-attachments">
                            {item.attachments.map((attachment, idx) => (
                              <div key={idx} className="attachment-item">
                                <div className="attachment-icon">
                                  {attachment.type === 'youtubeVideo' ? <FaVideo color="#ff0000" /> :
                                   attachment.type === 'driveFile' ? 
                                     (attachment.isCompressed ? <FaFileArchive color="#666" /> : <FaFile color="#4285f4" />) :
                                   attachment.type === 'link' ? <FaLink color="#0a66c2" /> :
                                   <FaFile color="#666" />}
                                </div>
                                <div className="attachment-info">
                                  <a 
                                    href={attachment.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="attachment-link"
                                    onClick={(e) => {
                                      if (!attachment.url || attachment.url === '#') {
                                        e.preventDefault();
                                        toast.error('This file is not accessible');
                                      }
                                    }}
                                  >
                                    {attachment.title}
                                    {attachment.isCompressed && " (Compressed)"}
                                  </a>
                                  {attachment.mimeType && (
                                    <span className="file-type">{attachment.mimeType}</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="item-actions">
                        <button 
                          className="btn-view"
                          onClick={() => handleViewItem(item)}
                        >
                          <FaEye /> View
                        </button>
                        {courseDetails.courseInfo.id && item.id && (
                          <button 
                            className="btn-view"
                            onClick={() => window.open(
                              `https://classroom.google.com/c/${courseDetails.courseInfo.id}/a/${item.id}`,
                              '_blank'
                            )}
                          >
                            <FaGoogle /> Open in Classroom
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* SHOW STATS */}
              <div className="classwork-stats-footer">
                <div className="stats-summary">
                  <p>
                    Showing <strong>{searchedItems.length}</strong> of <strong>{courseDetails.classwork.allItems.length}</strong> total items
                    {selectedTopic !== 'all' && ` in "${defaultTopics.find(t => t.id === selectedTopic)?.name}"`}
                    {searchTerm && ` matching "${searchTerm}"`}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

  // Render People Tab
  const renderPeopleTab = () => {
    if (!courseDetails?.people) {
      return (
        <div className="empty-state">
          <FaUserFriends size={48} color="#ddd" />
          <h4>No people data available</h4>
          <p>Unable to load teachers and students information.</p>
        </div>
      );
    }

    const { teachers = [], students = [] } = courseDetails.people;

    return (
      <div className="people-tab">
        <button

              className="btn-primary"

              onClick={() => window.open(courseDetails.courseInfo.alternateLink, '_blank')}

            >

              <FaBell /> Add People

            </button>
        {/* Teachers Section */}
        <div className="people-section">
          <h3>Teachers ({teachers.length})</h3>
          <div className="teachers-grid">
            {teachers.map(teacher => (
              <div key={teacher.userId} className="person-card teacher-card">
                
                <div className="person-info">
                  <h4>{teacher.fullName}</h4>
                  <p className="person-email">{teacher.email}</p>
                  <span className="person-role">Teacher</span>
                </div>
                <div className="person-actions">
                  <button 
                    className="email-btn"
                    onClick={() => window.open(`mailto:${teacher.email}`)}
                  >
                    <FaEnvelope /> Email
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Students Section */}
        <div className="people-section">
          <div className="section-header">
            <h3>Students ({students.length})</h3>
          
          </div>
          <div className="students-table">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {students.map(student => (
                  <tr key={student.userId}>
                    <td>
                      <div className="student-info">
                        
                        <span className="student-name">{student.fullName}</span>
                      </div>
                    </td>
                    <td>{student.email}</td>
                    <td>
                      <div className="student-actions">
                        <button 
                          className="btn-small"
                          onClick={() => window.open(`mailto:${student.email}`)}
                        >
                          <FaEnvelope />
                        </button>
                        
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // Helper functions
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="teacher-google-wrapper">
      <div className="google-header">
        <h2>
          <FaGoogle style={{ color: "#ffffff" }} />  Google Classroom
        </h2>

        {googleAuthenticated ? (
          <div className="auth-controls">
            <span className="status-badge connected">
              <FaCheckCircle /> Connected
            </span>
            <button className="btn-secondary" onClick={fetchGoogleCourses}>
              Refresh Courses
            </button>
            <button
              className="btn-warning"
              onClick={handleGoogleReAuth}
            >
              <FaGoogle /> Re-authenticate
            </button>
            {/* <button
              className="btn-warning"
              onClick={disconnectGoogle}
            >
              Disconnect
            </button> */}
          </div>
        ) : (
          <button className="btn-view" onClick={handleGoogleAuth}>
            <FaGoogle /> Connect Google Classroom
          </button>
        )}
      </div>

      {/* If not connected */}
      {!googleAuthenticated && (
        <div className="google-auth-prompt">
          <FaGoogle size={60} color="#4285F4" />
          <h4>Connect your Google Classroom</h4>
          <p>
            Sign in with your Gmail to load your Google Classroom courses.
          </p>
          <button className="btn-view" onClick={handleGoogleAuth}>
            <FaGoogle /> Connect Google Classroom
          </button>
        </div>
      )}

      {/* Course List */}
      {googleAuthenticated && (
        <div className="google-courses-section">
          <h3>Your Google Classroom Courses</h3>

          {loadingGoogle ? (
            <div className="loading-courses">
              <FaSpinner className="spinner" />
              <p>Loading your courses...</p>
            </div>
          ) : googleCourses.length === 0 ? (
            <div className="no-courses">
              <FaBook size={48} color="#ddd" />
              <p>No Google Classroom courses found.</p>
              <button className="btn-view" onClick={fetchGoogleCourses}>
                Try Again
              </button>
            </div>
          ) : (
            <div className="courses-grid">
              {googleCourses.map((course) => (
                <div
                  key={course.googleId || course.id}
                  className="course-card"
                >
                  <div className="course-header">
                    <h5>{course.name}</h5>
                    <span className="course-code">Class code: {course.enrollmentCode}</span>
                  </div>
                  <p className="course-section">{course.section || "No section"}</p>
                  {course.description && (
                    <p className="course-description">{course.description.substring(0, 100)}...</p>
                  )}

                  <div className="course-actions">
                    <button
                      className="btn-view"
                      onClick={() => fetchCourseDetails(course.googleId || course.id)}
                    >
                      <FaEye /> View Details
                    </button>
                    <button
                      className="btn-view"
                      onClick={() =>
                        window.open(
                          course.alternateLink ||
                            `https://classroom.google.com/c/${course.googleId || course.id}`,
                          "_blank"
                        )
                      }
                    >
                      <FaGoogle /> Open Classroom
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Course Details with Tabs */}
      {courseDetails && (
        <div className="course-details-panel">
          <div className="details-header">
            <div className="course-title-section">
              <h3>{courseDetails.courseInfo.name}</h3>
              <p className="course-section">{courseDetails.courseInfo.section || "No section"}</p>
              <div className="course-code-display">
                Class code: <strong>{courseDetails.courseInfo.enrollmentCode}</strong>
              </div>
            </div>
            <button
              className="btn-secondary"
              onClick={() => setCourseDetails(null)}
            >
              Close
            </button>
          </div>

          {/* Tabs Navigation */}
          <div className="course-tabs">
            <button 
              className={`tab-button ${activeTab === "stream" ? "active" : ""}`}
              onClick={() => setActiveTab("stream")}
            >
              <FaStream /> Stream ({courseDetails.stream?.count || 0})
            </button>
            <button 
              className={`tab-button ${activeTab === "classwork" ? "active" : ""}`}
              onClick={() => setActiveTab("classwork")}
            >
              <FaTasks /> Classwork ({courseDetails.classwork?.count || 0})
            </button>
            <button 
              className={`tab-button ${activeTab === "people" ? "active" : ""}`}
              onClick={() => setActiveTab("people")}
            >
              <FaUserFriends /> People ({courseDetails.people?.studentCount + courseDetails.people?.teacherCount || 0})
            </button>
          </div>

          {/* Tab Content */}
          <div className="tab-content">
            {loadingGoogle ? (
              <div className="loading-tab">
                <FaSpinner className="spinner" />
                <p>Loading {activeTab} data...</p>
              </div>
            ) : (
              <>
                {activeTab === "stream" && renderStreamTab()}
                {activeTab === "classwork" && renderClassworkTab()}
                {activeTab === "people" && renderPeopleTab()}
              </>
            )}
          </div>

          {/* Footer Stats */}
          <div className="course-footer">
            <div className="footer-stats">
              <div className="stat-item">
                <FaUsers /> <span>{courseDetails.stats?.totalStudents || 0} Students</span>
              </div>
              <div className="stat-item">
                <FaFileAlt /> <span>{courseDetails.stats?.totalAssignments || 0} Assignments</span>
              </div>
              <div className="stat-item">
                <FaBell /> <span>{courseDetails.stats?.totalAnnouncements || 0} Announcements</span>
              </div>
              <div className="stat-item">
                <FaBook /> <span>{courseDetails.stats?.totalMaterials || 0} Materials</span>
              </div>
            </div>
            <div className="footer-info">
              <span>Last updated: {new Date().toLocaleDateString()}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoogleClassroom;